import React, { useState } from "react";
import { useAuth } from "../../context/AuthContext";
import axios from "axios";
import "./UploadCertificatesPage.css"; // Asegúrate de que este archivo CSS exista y esté actualizado

// URL base para los logos por defecto (ej. ministerio_logo.png) - Referencia visual o para enviar al backend
const DEFAULT_LOGO_PATH_FOR_FRONTEND = "logos/ministerio_logo.png";

function UploadCertificatesPage() {
  // --- Estados del formulario y proceso de subida ---
  const [selectedFile, setSelectedFile] = useState(null);
  const [message, setMessage] = useState(""); // Mensajes de éxito general
  const [error, setError] = useState(""); // Mensajes de error general
  const [isLoading, setIsLoading] = useState(false); // Estado de carga principal

  // --- Estados para el resumen del procesamiento (recibidos del backend) ---
  const [summary, setSummary] = useState(null);
  const [organizersWithoutLogo, setOrganizersWithoutLogo] = useState([]);
  const [participantsWithoutEmail, setParticipantsWithoutEmail] = useState([]);
  const [incompleteRecordsForDownload, setIncompleteRecordsForDownload] =
    useState([]); // Datos para el reporte a descargar
  const [emailsReadyToSend, setEmailsReadyToSend] = useState([]); // NUEVO: Cola de correos para enviar

  const { token } = useAuth(); // Obtiene el token de autenticación del contexto
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL; // URL base de tu API backend

  // Maneja el cambio de archivo seleccionado en el input
  const handleFileChange = (event) => {
    setSelectedFile(event.target.files[0]); // Captura el archivo seleccionado
    setMessage("");
    setError("");
    // Limpiar resultados anteriores al seleccionar un nuevo archivo
    setSummary(null);
    setOrganizersWithoutLogo([]);
    setParticipantsWithoutEmail([]);
    setIncompleteRecordsForDownload([]);
    setEmailsReadyToSend([]); // Limpiar cola de correos
  };

  // Maneja el envío del formulario de subida del archivo Excel
  const handleSubmit = async (event) => {
    event.preventDefault(); // Previene el recargo de la página por defecto

    if (!selectedFile) {
      setError("Por favor, selecciona un archivo Excel para subir.");
      return;
    }

    setIsLoading(true);
    setMessage("");
    setError("");
    setSummary(null);
    setOrganizersWithoutLogo([]);
    setParticipantsWithoutEmail([]);
    setIncompleteRecordsForDownload([]);
    setEmailsReadyToSend([]); // Limpiar antes de la nueva subida

    // FormData es necesario para enviar archivos a través de HTTP
    const formData = new FormData();
    formData.append("excelFile", selectedFile); // 'excelFile' debe coincidir con el nombre del campo que Multer espera en el backend

    try {
      // Petición POST al endpoint de procesamiento de Excel del backend
      const response = await axios.post(
        `${API_BASE_URL}/upload/participants-excel`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`, // Envía el token JWT para autenticación
          },
        }
      );

      if (response.status !== 200) {
        const errorData = response.data;
        throw new Error(
          errorData.error || "Error al procesar el archivo Excel."
        );
      }

      const data = response.data; // Datos de la respuesta del backend
      setMessage(data.message || "Archivo procesado exitosamente.");

      // Actualizar estados con el resumen recibido del backend
      setSummary(data.summary);
      setOrganizersWithoutLogo(data.organizersWithoutLogo || []);
      setParticipantsWithoutEmail(data.summary?.participantsWithoutEmail || []);
      setIncompleteRecordsForDownload(
        data.summary?.incompleteRecordsForReport || []
      );
      setEmailsReadyToSend(data.summary?.emailsReadyToSend || []); // Guardar la cola de correos

      console.log("Respuesta completa del backend:", data);
      setSelectedFile(null); // Limpiar el archivo seleccionado del input
    } catch (err) {
      setError(err.response?.data?.error || "Error al subir el archivo Excel.");
      console.error(
        "Error al subir el archivo Excel:",
        err.response?.data || err.message
      );
    } finally {
      setIsLoading(false); // Finalizar el estado de carga
    }
  };

  // Función para descargar el reporte de registros incompletos
  const handleDownloadReport = async () => {
    if (incompleteRecordsForDownload.length === 0) {
      setError("No hay registros incompletos para descargar.");
      return;
    }

    try {
      const response = await axios.post(
        `${API_BASE_URL}/upload/incomplete-report`,
        {
          incompleteRecords: incompleteRecordsForDownload,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          responseType: "blob",
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      const contentDisposition = response.headers["content-disposition"];
      let filename = `reporte_registros_incompletos.xlsx`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1];
        }
      }
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setMessage("Reporte descargado exitosamente.");
    } catch (err) {
      setError(err.response?.data?.error || "Error al descargar el reporte.");
      console.error(
        "Error downloading report:",
        err.response?.data || err.message
      );
    }
  };

  // NUEVA FUNCIÓN: Enviar correos de certificados
  const handleSendEmails = async () => {
    if (emailsReadyToSend.length === 0) {
      setError("No hay correos listos para enviar.");
      return;
    }

    if (
      !window.confirm(
        `¿Está seguro de que desea enviar ${emailsReadyToSend.length} correos de certificados? Este proceso puede tardar.`
      )
    ) {
      return;
    }

    setMessage("Iniciando envío de correos...");
    setError("");
    setIsLoading(true); // Opcional: mostrar un loader diferente para el envío de correos

    try {
      const response = await axios.post(
        `${API_BASE_URL}/upload/send-emails`,
        {
          emailsToSend: emailsReadyToSend, // Enviar la cola de correos al nuevo endpoint
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      const results = response.data.results;
      setMessage(
        `Envío de correos completado: ${results.success} exitosos, ${results.failed} fallidos.`
      );
      if (results.failed > 0) {
        setError(
          `Algunos correos no pudieron enviarse. Revise el log del servidor para más detalles.`
        );
      }
      setEmailsReadyToSend([]); // Limpiar la cola después de intentar el envío
    } catch (err) {
      setError(err.response?.data?.error || "Error al enviar los correos.");
      console.error("Error sending emails:", err.response?.data || err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="upload-container">
      <div className="upload-card">
        <h2 className="upload-title">Cargar Certificados (Excel)</h2>
        <p className="upload-description">
          Sube un archivo Excel (.xlsx o .xls) con la información de los
          participantes y certificados.
        </p>

        {message && <p className="upload-message success">{message}</p>}
        {error && <p className="upload-message error">{error}</p>}

        <form onSubmit={handleSubmit} className="upload-form">
          <input
            type="file"
            accept=".xlsx, .xls"
            onChange={handleFileChange}
            className="upload-input-file"
            disabled={isLoading}
          />
          {selectedFile && (
            <p className="selected-file-info">
              Archivo seleccionado: {selectedFile.name}
            </p>
          )}

          <button type="submit" disabled={isLoading} className="upload-button">
            {isLoading ? "Subiendo y procesando..." : "Subir y Procesar Excel"}
          </button>
        </form>

        {isLoading && <p className="upload-loading">Procesando archivo...</p>}

        {summary && (
          <div className="upload-summary">
            <h3>Resumen del Procesamiento:</h3>
            <p>
              <strong>Participantes creados:</strong>{" "}
              {summary.participantsCreated}
            </p>
            <p>
              <strong>Participantes existentes:</strong>{" "}
              {summary.participantsExisting}
            </p>
            <p>
              <strong>Organizadores creados:</strong>{" "}
              {summary.organizersCreated}
            </p>
            <p>
              <strong>Organizadores existentes:</strong>{" "}
              {summary.organizersExisting}
            </p>
            <p>
              <strong>Eventos creados:</strong> {summary.eventsCreated}
            </p>
            <p>
              <strong>Eventos existentes:</strong> {summary.eventsExisting}
            </p>
            <p>
              <strong>Certificados creados:</strong>{" "}
              {summary.certificatesCreated}
            </p>
            <p>
              <strong>Certificados duplicados:</strong>{" "}
              {summary.certificatesDuplicate}
            </p>
            <p>
              <strong>Certificados saltados (sin logo de organizador):</strong>{" "}
              {summary.skippedCertificatesNoLogo}
            </p>
            <p>
              <strong>Registros saltados (datos incompletos):</strong>{" "}
              {summary.skippedCertificatesIncompleteData}
            </p>
            <p>
              <strong>Participantes sin correo (no notificados):</strong>{" "}
              {summary.skippedCertificatesNoEmail}
            </p>

            {organizersWithoutLogo.length > 0 && (
              <div className="organizers-no-logo">
                <h4>Organizadores sin logo (certificados saltados):</h4>
                <ul>
                  {organizersWithoutLogo.map((org, index) => (
                    <li key={index}>{org}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Botones de acción post-procesamiento */}
            <div className="post-upload-actions">
              {incompleteRecordsForDownload.length > 0 && (
                <button
                  onClick={handleDownloadReport}
                  className="download-report-button"
                >
                  <span className="material-icons">download</span> Descargar
                  Reporte de Errores ({incompleteRecordsForDownload.length})
                </button>
              )}
              {emailsReadyToSend.length > 0 && ( // Mostrar botón de enviar correos si hay correos en la cola
                <button
                  onClick={handleSendEmails}
                  className="send-emails-button"
                >
                  <span className="material-icons">email</span> Enviar Correos (
                  {emailsReadyToSend.length})
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default UploadCertificatesPage;
